'use client'

// Scroll-scrubbed reveal for the Process timeline.
//
// Sets a `--reveal-progress` CSS variable (0 → 1) on `.how-track` based
// on how far the section has scrolled through the viewport. The CSS in
// app/page.css drives line drawing, dot popping, and card fade-up off
// that variable, so scrolling forward plays the animation and scrolling
// backward reverses it.
//
// prefers-reduced-motion: skip the listener and snap progress to 1.

import { useEffect, useRef } from 'react'

export default function ProcessTrack({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      el.style.setProperty('--reveal-progress', '1')
      return
    }

    // Anchor progress to the *section* so the headline + cards are framed
    // when the animation completes, not when the track sticks to the top.
    const section = (el.closest('section') as HTMLElement) ?? el

    let raf = 0
    const update = () => {
      raf = 0
      const rect = section.getBoundingClientRect()
      const viewport = window.innerHeight
      // Progress = 0 when the section top hits the viewport bottom (entering).
      // Progress = 1 when the section is centered vertically in the viewport.
      // Total scroll travel = (viewport + section height) / 2, which is the
      // distance from "just entering" to "centered".
      const range = (viewport + rect.height) / 2
      const distance = viewport - rect.top
      const raw = distance / range
      const clamped = Math.max(0, Math.min(1, raw))
      el.style.setProperty('--reveal-progress', clamped.toFixed(3))
    }

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    update()

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <div ref={ref} className="how-track">
      {children}
    </div>
  )
}
